import { useMemo } from 'react';

interface DurationProps {
  /** Duration in milliseconds */
  duration: number;
  /** Format style for the duration display */
  format?: 'compact' | 'full' | 'verbose';
  /** Whether to show milliseconds when duration is less than 1 second */
  showMilliseconds?: boolean;
  /** Custom className for styling */
  className?: string;
}

interface TimeComponents {
  hours: number;
  minutes: number;
  seconds: number;
  milliseconds: number;
}

/**
 * A React component that displays duration in a human-readable format
 * Supports multiple display formats and proper time formatting
 */
export function Duration({ 
  duration, 
  format = 'compact', 
  showMilliseconds = true,
  className = ''
}: DurationProps) {
  const timeComponents = useMemo((): TimeComponents => {
    const ms = Math.abs(duration) % 1000;
    const totalSeconds = Math.floor(Math.abs(duration) / 1000);
    const s = totalSeconds % 60;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const m = totalMinutes % 60;
    const h = Math.floor(totalMinutes / 60);

    return { hours: h, minutes: m, seconds: s, milliseconds: ms };
  }, [duration]);

  const formatTime = useMemo((): string => {
    const { hours, minutes, seconds, milliseconds } = timeComponents;
    const isNegative = duration < 0;
    const prefix = isNegative ? '-' : '';

    switch (format) {
      case 'verbose':
        return formatVerbose(hours, minutes, seconds, milliseconds, showMilliseconds, prefix);
      case 'full':
        return formatFull(hours, minutes, seconds, milliseconds, showMilliseconds, prefix);
      case 'compact':
      default:
        return formatCompact(hours, minutes, seconds, milliseconds, showMilliseconds, prefix);
    }
  }, [timeComponents, format, showMilliseconds, duration]);

  return (
    <time 
      dateTime={`PT${Math.abs(duration)}MS`}
      className={className}
      title={`Duration: ${formatTime}`}
    >
      {formatTime}
    </time>
  );
}

// Helper functions for different format styles
function formatVerbose(h: number, m: number, s: number, ms: number, showMs: boolean, prefix: string): string {
  const parts: string[] = [];
  
  if (h > 0) parts.push(`${h} hour${h !== 1 ? 's' : ''}`);
  if (m > 0) parts.push(`${m} minute${m !== 1 ? 's' : ''}`);
  if (s > 0 || (h === 0 && m === 0)) {
    if (showMs && h === 0 && m === 0 && ms > 0) {
      parts.push(`${s}.${ms.toString().padStart(3, '0')} second${s !== 1 || ms > 0 ? 's' : ''}`);
    } else {
      parts.push(`${s} second${s !== 1 ? 's' : ''}`);
    }
  }
  
  return prefix + parts.join(', ');
}

function formatFull(h: number, m: number, s: number, ms: number, showMs: boolean, prefix: string): string {
  if (h > 0) {
    return `${prefix}${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  if (m > 0) {
    return `${prefix}${m}:${s.toString().padStart(2, '0')}`;
  }
  if (showMs && ms > 0) {
    return `${prefix}${s}.${ms.toString().padStart(3, '0')}s`;
  }
  return `${prefix}${s}s`;
}

function formatCompact(h: number, m: number, s: number, ms: number, showMs: boolean, prefix: string): string {
  if (h > 0) {
    return `${prefix}${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  if (m > 0) {
    return `${prefix}${m}:${s.toString().padStart(2, '0')}`;
  }
  if (showMs && s < 10 && ms > 0) {
    return `${prefix}${s}.${Math.floor(ms / 10).toString().padStart(2, '0')}s`;
  }
  return `${prefix}${s}s`;
}